import React from 'react';
import DropUpload from "../../../Submission/BatchUpload/DropUpload";
import { DROPUPLOAD_PROPS, FILE } from "../../../Submission/BatchUpload/Types";
import classNames from "classnames";
import KendoGridAbstract from "../../../util/KendoGridAbstract/KendoGridAbstract";
import { GridCellProps, GridColumn } from "@progress/kendo-react-grid";
import { defaultCellRender } from "../../../util/Util";
import { ComboWithValue, ProgressBar, PuzzleButton } from "puzzle";
import { Keys } from "@progress/kendo-react-common";
import _ from "lodash";
import { COLLATERAL_FILES, DATA_ITEM, LANGUAGE } from "./Types";
import { ComboBoxCloseEvent, ComboBoxFocusEvent, ComboBoxBlurEvent } from "@progress/kendo-react-dropdowns";

type SUBMISSION_LEVEL = {
  targetLanguage: string
  targetLanguageDisplayName: string
}

type PROPS = DROPUPLOAD_PROPS & {
  taskId: number,
  languages: LANGUAGE[],
  filesToUpload: COLLATERAL_FILES[]
};

class SupplementaryFilesUploadGrid extends DropUpload<PROPS> {
  submissionLevel: SUBMISSION_LEVEL = {
    targetLanguage: 'SUBMISSION',
    targetLanguageDisplayName: 'Submission'
  };

  tooltip: any

  public state: any = {
    ...this.state, targetLanguages: [this.submissionLevel],
    editFileTicket: null
  };

  static defaultProps = {
    moduleName: 'DropUpload',
    uploadActionName: 'uploadSupplementaryFiles'
  };

  /**
   * @inheritdoc
   */
  childModuleDidMount = () => {
    let { targetLanguages } = this.state;
    const { languages } = this.props;

    const availableLanguages = languages.map((item: LANGUAGE) => {
      return {
        targetLanguage: item.locale,
        targetLanguageDisplayName: item.value
      }
    });
    const sortedLanguages = _.sortBy(availableLanguages, ['targetLanguageDisplayName']);
    targetLanguages.push(...sortedLanguages);

    // @ts-ignore
    this.setState({ targetLanguages })
    if (this.props.filesToUpload.length) {
      this.startFileUpload(this.props.filesToUpload, 0);
    }
  };

  /**
   * Closes edit mode and fills level dropdown if empty
   */
  exitEdit = (event: ComboBoxBlurEvent | any) => {
    event.nativeEvent.target?.classList?.remove('k-focused');
  };

  /**
   * Returns data for save
   */
  getData = () => {
    const { droppedFiles } = this.state;
    const { taskId, submissionId } = this.props
    const supplementaryFiles = droppedFiles.map(({ level, repositoryTicket }: any) => {
      return {
        targetLanguageLocale: level === "SUBMISSION" ? null : level,
        repositoryTicket
      };
    });
    return {
      params: {
        supplementaryFiles: supplementaryFiles
      },
      taskId,
      submissionId
    };
  };

  /**
   * Item change handler. Set selected target level to file
   */
  itemChange = (cellProps: GridCellProps, event: ComboBoxCloseEvent) => {
    let value = event.target.value;

    if (value) {
      let dataItem = cellProps.dataItem;
      let field = cellProps.field;

      let droppedFiles = _.cloneDeep(this.state.droppedFiles);
      let droppedLength = droppedFiles.length;
      let current;
      while (droppedLength) {
        droppedLength--;
        current = droppedFiles[droppedLength];
        if (current.repositoryTicket === dataItem.repositoryTicket) {
          if (field) {
            droppedFiles[droppedLength][field] = value;
            break;
          }
        }
      }

      this.setState({
        droppedFiles: droppedFiles
      });
    }
  };

  /**
   * Exit edit mode if key ESC is pressed
   */
  targetLanguageComboKeyHandler = (dataItem: DATA_ITEM, event: React.KeyboardEvent) => {
    const { keyCode } = event;
    if (keyCode === Keys.esc) {
      this.exitEdit(event);
    }
  };

  /**
   * status row cell renderer
   */
  levelRenderer = (cellProps: GridCellProps) => {
    let dataItem = cellProps.dataItem;
    let field = cellProps.field;
    let value = field ? dataItem[field] : '';

    return (
      <>
          <span onKeyDown={this.targetLanguageComboKeyHandler.bind(this, dataItem)}>
            <ComboWithValue
              data={this.state.targetLanguages}
              defaultValue={value}
              onFocus={this.enterEdit}
              onBlur={this.exitEdit}
              onClose={this.itemChange.bind(this, cellProps)}
              textField={'targetLanguageDisplayName'}
              valueField={'targetLanguage'}
            />
          </span>

      </>
    );
  };

  /**
   * Delete button row cell renderer
   */
  deleteRowRenderer = (cellProps: GridCellProps) => {
    const { dataItem } = cellProps;

    return (
      <PuzzleButton
        aria-label={this.translate('SRL-deleteFile', { fileName: dataItem.fileName })}
        titleText={this.translate('deleteFile')}
        onClick={this.removeFile.bind(this, [cellProps.dataItem])}
        iconClass={'tp-icon tp-delete'}
        fillMode={'flat'}
        size={'small'}
        tooltipPosition={'right'}
        withTooltip
      />
    );
  }

  /**
   * status row cell renderer
   */
  statusRenderer = (cellProps: GridCellProps) => {
    const { progresses = {} } = this.state;
    let { moduleId } = this.props;
    return (
      <>
        <div className={'tp-progress-cell-inner-wrap'}>
          <ProgressBar
            key={`ProgressBar_${moduleId}_${cellProps.dataItem.fileName}`}
            className={'tp-uploaded-list-item-status k-text-ellipsis'}
            file={cellProps.dataItem}
            fileName={cellProps.dataItem.fileName}
            group={moduleId}
            progresses={progresses}
          />
        </div>
      </>
    )
  };

  /**
   * Grid cell render
   * @param defaultRendering
   * @param cellProps
   */
  cellRender = (defaultRendering: React.ReactElement<HTMLTableCellElement> | null, cellProps: GridCellProps): JSX.Element => {
    const { dataItem, field } = cellProps;
    if (defaultRendering && field) {
      let children;
      let editedProps = {
        ...defaultRendering.props
      };

      switch (cellProps.field) {
        case 'fileName':
          editedProps.className += '';
          children = defaultCellRender(this.translate, () => null, cellProps);
          break;
        case 'fileStatus':
          children = this.statusRenderer(cellProps);
          break;
        case 'level':
          children = this.levelRenderer(cellProps);
          break;
        case 'delete':
          children = this.deleteRowRenderer(cellProps);
          editedProps.className += ' k-text-right';
          break;
        default:
          children = dataItem[field];
          break;
      }

      return React.cloneElement(defaultRendering, editedProps, children);
    }
    return <></>
  };

  /**
   * @inheritDoc
   */
  createUploadedFilesList = () => {
    const { droppedFiles, dragging, editFileTicket } = this.state;
    let gridItems = droppedFiles.map((item: FILE) => {
      if (item.uploaded !== DropUpload.UploadStatus.ALREADY_UPLOADED) {
        // @ts-ignore
        item.inEdit = item.repositoryTicket === editFileTicket;
      }
      return item;
    });
    const classes = classNames("tp-uploaded-files-grid", {
      "tp-masked": dragging
    });
    return (
      <KendoGridAbstract
        captionText={this.translate('SRL-referenceFilesGridCaptionText')}
        rowHeight={40}
        className={classes}
        data={gridItems}
        editField={'inEdit'}
        cellRender={this.cellRender}
      >
        <GridColumn
          field="fileName"
          editable={false}
          title={this.translate('fileName')}
        />
        <GridColumn
          field="fileStatus"
          editable={false}
          title={this.translate('fileStatus')}
        />
        <GridColumn
          field="level"
          title={this.translate('level')}
        />
        <GridColumn
          headerClassName={'k-text-right'}
          field="delete"
          title={this.translate('columnHeaders.action')}
        />

      </KendoGridAbstract>
    );
  };

  /**
   * @inheritdoc
   */
  createUploadStatusSection = () => {
    const { droppedFiles } = this.state;
    return (
      <div className={'tp-files-uploaded-info-bar'}>
        {!!droppedFiles.length &&
          <div className=''>
            <PuzzleButton
              iconClass={'tp-icon tp-add-circle'}
              fillMode='flat'
              themeColor={'primary'}
              onClick={this.fileUploadHandler}
            >
              {this.translate('addMoreFiles')}
            </PuzzleButton>
          </div>
        }
      </div>
    )
  };


  /**
   * Enter in edit mode
   */
  enterEdit = (event: ComboBoxFocusEvent | any) => {
    event.nativeEvent.target?.classList?.add('k-focused');
  };

  /**
   * @inheritdoc
   *
   *  Adds additional data to dropped files
   */
  findFilesForUpdate = (uploadFiles: any[], droppedFile: any) => {
    let findUploadedFile = uploadFiles.find((file: any) => {
      let status = droppedFile.uploaded !== DropUpload.UploadStatus.FAILED && droppedFile.uploaded !== DropUpload.UploadStatus.ALREADY_UPLOADED;
      return status && !droppedFile.repositoryTicket && droppedFile.fileName === file.name;
    });
    if (findUploadedFile) {
      droppedFile = {
        fileName: findUploadedFile.name,
        uploaded: DropUpload.UploadStatus.UPLOADED,
        level: 'SUBMISSION'
      }
    }

    return droppedFile;
  };
}

export default SupplementaryFilesUploadGrid;
